import React, { useState, useEffect, useCallback } from 'react'
import api from '../../services/api'
import { toast } from 'react-toastify'
import '../../css/instagram.css'

const fmt = (d) => (d ? new Date(d).toLocaleString('it-IT') : '—')

/* ──────────────────────────────────────────────────────── */
/*  Form modale per creare/editare una regola               */
/* ──────────────────────────────────────────────────────── */
function RuleForm({ rule, onSave, onCancel }) {
  const [igMediaId, setIgMediaId] = useState(rule?.igMediaId ?? '')
  const [igMediaUrl, setIgMediaUrl] = useState(rule?.igMediaUrl ?? '')
  const [keyword, setKeyword] = useState(rule?.keyword ?? '')
  const [responseMessage, setResponseMessage] = useState(rule?.responseMessage ?? '')
  const [publicReply, setPublicReply] = useState(rule?.publicReply ?? '')
  const [skipAlreadySent, setSkipAlreadySent] = useState(rule?.skipAlreadySent ?? true)
  const [saving, setSaving] = useState(false)
  const [resolving, setResolving] = useState(false)

  const handleUrlChange = async (e) => {
    const url = e.target.value
    setIgMediaUrl(url)

    // Auto-resolve media ID quando si incolla un URL Instagram valido
    if (url.match(/instagram\.com\/(p|reel|tv)\/[A-Za-z0-9_-]+/)) {
      setResolving(true)
      try {
        const { data } = await api.get('/instagram-resolve-media', { params: { url } })
        setIgMediaId(data.mediaId)
        toast.success(`Media ID trovato: ${data.mediaId}`, { theme: 'dark' })
      } catch (err) {
        toast.error(err?.response?.data?.error || 'Impossibile risolvere Media ID', { theme: 'dark' })
      } finally {
        setResolving(false)
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!igMediaId.trim() || !keyword.trim() || !responseMessage.trim()) {
      toast.error('Compila tutti i campi obbligatori', { theme: 'dark' })
      return
    }
    setSaving(true)
    try {
      const payload = {
        igMediaId: igMediaId.trim(),
        igMediaUrl: igMediaUrl.trim() || null,
        keyword: keyword.trim(),
        responseMessage: responseMessage.trim(),
        publicReply: publicReply.trim() || null,
        skipAlreadySent,
      }
      if (rule) {
        await api.put(`/instagram-rules/${rule.id}`, payload)
        toast.success('Regola aggiornata', { theme: 'dark' })
      } else {
        await api.post('/instagram-rules', payload)
        toast.success('Regola creata', { theme: 'dark' })
      }
      onSave()
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Errore salvataggio', { theme: 'dark' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="ig-form-backdrop" onClick={onCancel}>
      <form className="ig-form" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
        <h3>{rule ? 'Modifica Regola' : 'Nuova Regola'}</h3>

        <label>
          URL post <span className="ig-required">*</span>
          <small className="ig-field-hint">Incolla l'URL del post — il Media ID viene estratto automaticamente</small>
          <input
            type="text"
            value={igMediaUrl}
            onChange={handleUrlChange}
            placeholder="https://www.instagram.com/p/..."
            disabled={resolving}
          />
          {resolving && <small className="ig-resolving">Ricerca Media ID in corso...</small>}
        </label>

        <label>
          Media ID <span className="ig-required">*</span>
          <input
            type="text"
            value={igMediaId}
            onChange={(e) => setIgMediaId(e.target.value)}
            placeholder="Auto-compilato dall'URL, oppure inserisci manualmente"
          />
        </label>

        <label>
          Keyword <span className="ig-required">*</span>
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="Parola da matchare (exact word)"
          />
        </label>

        <label>
          Messaggio DM privato <span className="ig-required">*</span>
          <small className="ig-field-hint">Max 1000 caratteri (limite Meta)</small>
          <textarea
            rows={4}
            value={responseMessage}
            onChange={(e) => setResponseMessage(e.target.value)}
            maxLength={1000}
            placeholder="Messaggio da inviare in DM (breve, invita a rispondere)"
          />
          <small className={`ig-char-count ${responseMessage.length > 900 ? 'ig-char-count--warn' : ''}`}>
            {responseMessage.length}/1000
          </small>
        </label>

        <label>
          Risposta pubblica al commento
          <small className="ig-field-hint">Visibile sotto il commento — notifica l'utente che ha un DM</small>
          <textarea
            rows={2}
            value={publicReply}
            onChange={(e) => setPublicReply(e.target.value)}
            placeholder="Es. Ti ho mandato un messaggio! 📩 (opzionale)"
          />
        </label>

        <label className="ig-checkbox-label">
          <input
            type="checkbox"
            checked={skipAlreadySent}
            onChange={(e) => setSkipAlreadySent(e.target.checked)}
          />
          <span>Non inviare DM a utenti già contattati</span>
          <small className="ig-field-hint">Se attivo, ogni utente riceve al massimo un DM per questa regola</small>
        </label>

        <div className="ig-form-actions">
          <button type="button" className="ig-btn ig-btn--secondary" onClick={onCancel} disabled={saving}>
            Annulla
          </button>
          <button type="submit" className="ig-btn ig-btn--primary" disabled={saving}>
            {saving ? 'Salvando...' : rule ? 'Aggiorna' : 'Crea'}
          </button>
        </div>
      </form>
    </div>
  )
}

/* ──────────────────────────────────────────────────────── */
/*  Tab Regole                                              */
/* ──────────────────────────────────────────────────────── */
function RulesTab() {
  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null) // null = chiuso, {} = nuova, {id,...} = edit

  const fetchRules = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/instagram-rules')
      setRules(data)
    } catch {
      toast.error('Errore caricamento regole', { theme: 'dark' })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchRules() }, [fetchRules])

  const handleToggle = async (id) => {
    try {
      await api.patch(`/instagram-rules/${id}/toggle`)
      fetchRules()
    } catch {
      toast.error('Errore toggle', { theme: 'dark' })
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa regola?')) return
    try {
      await api.delete(`/instagram-rules/${id}`)
      toast.success('Regola eliminata', { theme: 'dark' })
      fetchRules()
    } catch {
      toast.error('Errore eliminazione', { theme: 'dark' })
    }
  }

  return (
    <div className="ig-rules">
      <div className="ig-rules-header">
        <h2>Regole Auto-DM</h2>
        <button className="ig-btn ig-btn--primary" onClick={() => setEditing({})}>
          <i className="fas fa-plus" /> Nuova Regola
        </button>
      </div>

      {loading ? (
        <div className="ig-loader">Caricamento...</div>
      ) : rules.length === 0 ? (
        <p className="ig-empty">Nessuna regola configurata. Crea la prima!</p>
      ) : (
        <div className="ig-table-wrap">
          <table className="ig-table">
            <thead>
              <tr>
                <th>Media ID</th>
                <th>Keyword</th>
                <th>Risposta DM</th>
                <th>Stato</th>
                <th>DM inviati</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} className={r.isActive ? '' : 'ig-row-disabled'}>
                  <td className="ig-cell-media">
                    {r.igMediaUrl ? (
                      <a href={r.igMediaUrl} target="_blank" rel="noopener noreferrer" title={r.igMediaId}>
                        {r.igMediaId.length > 18 ? r.igMediaId.slice(0, 18) + '...' : r.igMediaId}
                      </a>
                    ) : (
                      <span title={r.igMediaId}>
                        {r.igMediaId.length > 18 ? r.igMediaId.slice(0, 18) + '...' : r.igMediaId}
                      </span>
                    )}
                  </td>
                  <td><code className="ig-keyword">{r.keyword}</code></td>
                  <td className="ig-cell-msg">
                    {r.responseMessage.length > 60
                      ? r.responseMessage.slice(0, 60) + '...'
                      : r.responseMessage}
                  </td>
                  <td>
                    <span className={`ig-badge ${r.isActive ? 'ig-badge--active' : 'ig-badge--inactive'}`}>
                      {r.isActive ? 'Attivo' : 'Disattivato'}
                    </span>
                  </td>
                  <td className="ig-cell-count">
                    <span className="ig-count-sent">{r.dmSentCount ?? 0}</span>
                    {(r.dmFailedCount ?? 0) > 0 && (
                      <span className="ig-count-failed"> / {r.dmFailedCount} err</span>
                    )}
                  </td>
                  <td className="ig-cell-actions">
                    <button
                      className="ig-btn-icon"
                      title={r.isActive ? 'Disattiva' : 'Attiva'}
                      onClick={() => handleToggle(r.id)}
                    >
                      <i className={`fas ${r.isActive ? 'fa-pause' : 'fa-play'}`} />
                    </button>
                    <button
                      className="ig-btn-icon"
                      title="Modifica"
                      onClick={() => setEditing(r)}
                    >
                      <i className="fas fa-edit" />
                    </button>
                    <button
                      className="ig-btn-icon ig-btn-icon--danger"
                      title="Elimina"
                      onClick={() => handleDelete(r.id)}
                    >
                      <i className="fas fa-trash" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <RuleForm
          rule={editing.id ? editing : null}
          onSave={() => { setEditing(null); fetchRules() }}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  )
}

/* ──────────────────────────────────────────────────────── */
/*  Tab Log DM                                              */
/* ──────────────────────────────────────────────────────── */
function LogsTab() {
  const [logs, setLogs] = useState([])
  const [meta, setMeta] = useState(null)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [filterStatus, setFilterStatus] = useState('')

  const fetchLogs = useCallback(async () => {
    setLoading(true)
    try {
      const params = { page, limit: 30 }
      if (filterStatus) params.status = filterStatus
      const { data } = await api.get('/instagram-logs', { params })
      setLogs(data.data ?? [])
      setMeta(data.meta ?? null)
    } catch {
      toast.error('Errore caricamento log', { theme: 'dark' })
    } finally {
      setLoading(false)
    }
  }, [page, filterStatus])

  useEffect(() => { fetchLogs() }, [fetchLogs])

  return (
    <div className="ig-logs">
      <div className="ig-logs-header">
        <h2>Log DM</h2>
        <div className="ig-logs-filters">
          <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1) }}>
            <option value="">Tutti</option>
            <option value="sent">Inviati</option>
            <option value="failed">Falliti</option>
            <option value="skipped">Skippati</option>
            <option value="queued">In coda</option>
            <option value="expired">Scaduti</option>
          </select>
          <button className="ig-btn ig-btn--secondary" onClick={fetchLogs}>
            <i className="fas fa-sync-alt" /> Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="ig-loader">Caricamento...</div>
      ) : logs.length === 0 ? (
        <p className="ig-empty">Nessun log trovato.</p>
      ) : (
        <>
          <div className="ig-table-wrap">
            <table className="ig-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Utente</th>
                  <th>Commento</th>
                  <th>Regola</th>
                  <th>Stato</th>
                  <th>Errore</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id}>
                    <td className="ig-cell-date">{fmt(l.createdAt)}</td>
                    <td>
                      <span className="ig-username">
                        {l.igUsername ?? l.igUserId}
                      </span>
                    </td>
                    <td className="ig-cell-msg">
                      {l.commentText?.length > 80
                        ? l.commentText.slice(0, 80) + '...'
                        : l.commentText}
                    </td>
                    <td>
                      {l.rule ? (
                        <code className="ig-keyword">{l.rule.keyword}</code>
                      ) : (
                        <span className="ig-muted">—</span>
                      )}
                    </td>
                    <td>
                      <span className={`ig-badge ig-badge--${l.status}`}>
                        {l.status}
                      </span>
                    </td>
                    <td className="ig-cell-err">
                      {l.errorMessage
                        ? l.errorMessage.length > 60
                          ? l.errorMessage.slice(0, 60) + '...'
                          : l.errorMessage
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {meta && meta.lastPage > 1 && (
            <div className="ig-pagination">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="ig-btn ig-btn--secondary"
              >
                &laquo; Prec
              </button>
              <span className="ig-page-info">
                Pagina {meta.currentPage} di {meta.lastPage}
              </span>
              <button
                disabled={page >= meta.lastPage}
                onClick={() => setPage((p) => p + 1)}
                className="ig-btn ig-btn--secondary"
              >
                Succ &raquo;
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

/* ──────────────────────────────────────────────────────── */
/*  Componenti Monitoraggio                                 */
/* ──────────────────────────────────────────────────────── */
function UsageGauge({ label, current, limit, pct }) {
  const getColor = (p) => {
    if (p >= 100) return '#ef4444'
    if (p >= 80) return '#eab308'
    return '#22c55e'
  }
  return (
    <div className="ig-gauge">
      <div className="ig-gauge-label">{label}</div>
      <div className="ig-gauge-bar-wrap">
        <div
          className="ig-gauge-bar-fill"
          style={{ width: `${Math.min(100, pct)}%`, backgroundColor: getColor(pct) }}
        />
      </div>
      <div className="ig-gauge-numbers">
        <span style={{ color: getColor(pct) }}>{current}</span>
        <span className="ig-muted"> / {limit}</span>
      </div>
    </div>
  )
}

function SummaryCard({ label, value, className }) {
  return (
    <div className={`ig-summary-card ${className}`}>
      <div className="ig-summary-value">{value}</div>
      <div className="ig-summary-label">{label}</div>
    </div>
  )
}

/* ──────────────────────────────────────────────────────── */
/*  Tab Monitoraggio API                                    */
/* ──────────────────────────────────────────────────────── */
function MonitoraggioTab() {
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/instagram-stats')
      setStats(data)
    } catch {
      toast.error('Errore caricamento statistiche', { theme: 'dark' })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStats()
    const interval = setInterval(fetchStats, 30_000)
    return () => clearInterval(interval)
  }, [fetchStats])

  const handleRetryQueue = async () => {
    try {
      const { data } = await api.post('/instagram-queue/retry-all')
      toast.info(data.message, { theme: 'dark' })
      fetchStats()
    } catch {
      toast.error('Errore retry coda', { theme: 'dark' })
    }
  }

  const handleClearExpired = async () => {
    if (!window.confirm('Rimuovere tutti i DM scaduti dalla coda?')) return
    try {
      const { data } = await api.post('/instagram-queue/clear-expired')
      toast.success(data.message, { theme: 'dark' })
      fetchStats()
    } catch {
      toast.error('Errore pulizia coda', { theme: 'dark' })
    }
  }

  if (loading) return <div className="ig-loader">Caricamento...</div>
  if (!stats) return <div className="ig-empty">Dati non disponibili</div>

  const { live, summary24h, tokenStatus } = stats
  const tokenDanger = tokenStatus?.valid === false || (
    tokenStatus?.daysRemaining !== null && tokenStatus?.daysRemaining <= 7
  )
  const tokenWarning = !tokenDanger && (
    tokenStatus?.valid === null || !tokenStatus?.expirationAvailable ||
    (tokenStatus?.daysRemaining !== null && tokenStatus?.daysRemaining <= 14)
  )
  const tokenTone = tokenDanger ? 'danger' : tokenWarning ? 'warning' : 'success'

  return (
    <div className="ig-monitoring">
      {live.throttling && (
        <div className="ig-alert ig-alert--warning">
          <i className="fas fa-exclamation-triangle" />
          <span>Throttling attivo — i DM vengono rallentati per rispettare i limiti Meta</span>
        </div>
      )}
      {!live.canSendDm && (
        <div className="ig-alert ig-alert--danger">
          <i className="fas fa-ban" />
          <span>Rate limit raggiunto — i nuovi DM vengono accodati automaticamente</span>
        </div>
      )}

      <div className="ig-gauges">
        <UsageGauge
          label="DM Privati (1h)"
          current={live.dm.sent}
          limit={live.dm.limit}
          pct={live.dm.pct}
        />
        <UsageGauge
          label="Risposte Commenti (1h)"
          current={live.commentReplies.sent}
          limit={live.commentReplies.limit}
          pct={live.commentReplies.pct}
        />
        <UsageGauge
          label="App Usage (Meta)"
          current={live.appUsage.call_count}
          limit={100}
          pct={live.appUsage.call_count}
        />
      </div>

      <div className="ig-queue-panel">
        <div className="ig-queue-header">
          <h3>Coda DM</h3>
          <div className="ig-queue-actions">
            <button className="ig-btn ig-btn--secondary" onClick={handleClearExpired}>
              <i className="fas fa-broom" /> Pulisci scaduti
            </button>
            <button className="ig-btn ig-btn--primary" onClick={handleRetryQueue}>
              <i className="fas fa-redo" /> Riprocessa coda
            </button>
          </div>
        </div>
        <div className="ig-queue-stat">
          <span className="ig-queue-count">{live.queueDepth}</span>
          <span className="ig-queue-label">DM in attesa</span>
        </div>
      </div>

      <div className="ig-summary-24h">
        <h3>Riepilogo 24h</h3>
        <div className="ig-summary-grid">
          <SummaryCard label="Inviati" value={summary24h.totalSent} className="ig-summary--sent" />
          <SummaryCard label="Falliti" value={summary24h.totalFailed} className="ig-summary--failed" />
          <SummaryCard label="Accodati" value={summary24h.totalQueued} className="ig-summary--queued" />
          <SummaryCard label="Scaduti" value={summary24h.totalExpired} className="ig-summary--expired" />
          <SummaryCard label="Totale" value={summary24h.totalAll} className="ig-summary--total" />
        </div>
      </div>

      <section className={`ig-token-panel ig-token-panel--${tokenTone}`}>
        <div className="ig-token-header">
          <div>
            <span className="ig-token-eyebrow">Credenziale Meta</span>
            <h3>Instagram Page Access Token</h3>
          </div>
          <span className={`ig-token-state ig-token-state--${tokenTone}`}>
            <i className={`fas ${tokenDanger ? 'fa-times-circle' : tokenWarning ? 'fa-exclamation-circle' : 'fa-check-circle'}`} />
            {tokenStatus?.valid === true ? 'Valido' : tokenStatus?.valid === false ? 'Non valido' : 'Da verificare'}
          </span>
        </div>

        <div className="ig-token-grid">
          <div className="ig-token-detail">
            <span>Account</span>
            <strong>{tokenStatus?.username ? `@${tokenStatus.username}` : '—'}</strong>
          </div>
          <div className="ig-token-detail">
            <span>Scadenza</span>
            <strong>{tokenStatus?.expiresAt ? fmt(tokenStatus.expiresAt) : 'Non disponibile'}</strong>
            {tokenStatus?.daysRemaining !== null && tokenStatus?.daysRemaining !== undefined && (
              <small>{tokenStatus.daysRemaining} giorni rimanenti</small>
            )}
          </div>
          <div className="ig-token-detail">
            <span>Ultima verifica</span>
            <strong>{fmt(tokenStatus?.checkedAt)}</strong>
          </div>
        </div>

        {tokenStatus?.checkError && tokenStatus?.valid !== true && (
          <p className="ig-token-note ig-token-note--danger">{tokenStatus.checkError}</p>
        )}
      </section>

      <p className="ig-refresh-info">
        <i className="fas fa-sync-alt" /> Aggiornamento automatico ogni 30 secondi
      </p>
    </div>
  )
}

/* ──────────────────────────────────────────────────────── */
/*  Pagina principale Instagram                             */
/* ──────────────────────────────────────────────────────── */
export default function InstagramPage() {
  const [tab, setTab] = useState('rules')

  const tabs = [
    { key: 'rules', label: 'Regole' },
    { key: 'logs',  label: 'Log DM' },
    { key: 'monitoring', label: 'Monitoraggio API' },
  ]

  return (
    <div className="ig-wrap">
      <nav className="ig-tabs" aria-label="Instagram tabs">
        <div className="ig-tabs__scroller">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`ig-tabs__btn ${tab === t.key ? 'is-active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </nav>

      {tab === 'rules' && <RulesTab />}
      {tab === 'logs' && <LogsTab />}
      {tab === 'monitoring' && <MonitoraggioTab />}
    </div>
  )
}
